import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Upload, X, Trash2, Pencil } from "lucide-react";
import { api, errMsg, imgSrc } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { H, Table } from "@/admin/AdminPages";

const romeParts = (d) => new Date(d.toLocaleString("en-US", { timeZone: "Europe/Rome" }));
const romeToIso = (local) => {
  const guess = new Date(`${local}:00Z`);
  const offset = romeParts(guess) - new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));
  return new Date(guess.getTime() - offset).toISOString();
};

const ImagePick = ({ label, value, onChange, testid, tall }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { onChange((await api.post("/admin/cms/upload", fd)).data.url); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div data-testid={testid}>
      <p className="label">{label}</p>
      <div className={`relative flex items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-[#D9D2EE] bg-[#F8F9FC] ${tall ? "h-44" : "h-32"}`}>
        {value ? <img src={imgSrc(value)} alt="" className="h-full w-full object-cover" /> : <span className="text-xs text-[#94A3B8]">Nessuna immagine</span>}
        {busy && <span className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 className="h-5 w-5 animate-spin" /></span>}
        {value && <button type="button" onClick={() => onChange("")} className="absolute right-2 top-2 rounded-full bg-white p-1 shadow" data-testid={`${testid}-remove`}><X className="h-4 w-4" /></button>}
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current.click()} className="btn-ghost mt-2 w-full !py-2 text-sm" data-testid={`${testid}-upload`}><Upload className="h-4 w-4" />Carica</button>
    </div>
  );
};

const AREA_LABELS = { outlet: "Juppo Outlet", market: "Juppo Market", scambi: "Juppo Scambi", pro: "Juppo Pro" };

const HomeImages = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-cms-home"], queryFn: async () => (await api.get("/admin/cms/home")).data });
  const [draft, setDraft] = useState(null);
  const s = draft || data;
  if (!s) return null;
  const upd = (fn) => setDraft((prev) => fn(prev || data));
  const save = async () => {
    try { await api.put("/admin/cms/home", s); setDraft(null); qc.invalidateQueries({ queryKey: ["admin-cms-home"] }); qc.invalidateQueries({ queryKey: ["cms-home"] }); toast.success("Immagini della home salvate"); } catch (e) { toast.error(errMsg(e)); }
  };
  const setSide = (i, v) => upd((b) => ({ ...b, side: b.side.map((x, j) => (j === i ? v : x)) }));
  return (
    <section className="card-soft p-6" data-testid="admin-home-images">
      <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-lg font-bold">Immagini della home</h2>
        <button onClick={save} disabled={!draft} className="btn-violet" data-testid="home-images-save-btn">Salva immagini</button></div>
      <p className="mt-1 text-sm text-[#64748B]">JPG, PNG o WEBP fino a 8 MB. L'immagine grande compare come sfondo in alto; lasciala vuota per usare lo sfondo sfumato.</p>
      <div className="mt-5 grid gap-5 lg:grid-cols-4">
        <div className="lg:col-span-4"><ImagePick tall label="Immagine grande in alto (sfondo)" value={s.background} onChange={(v) => upd((b) => ({ ...b, background: v }))} testid="cms-background" /></div>
        {s.side.map((v, i) => <ImagePick key={i} label={`Accanto alla ricerca ${i + 1}${i === 0 ? " (grande)" : ""}`} value={v} onChange={(x) => setSide(i, x)} testid={`cms-side-${i}`} />)}
        <div className="hidden lg:block" />
        {Object.keys(AREA_LABELS).map((k) => <ImagePick key={k} label={AREA_LABELS[k]} value={s.areas[k]} onChange={(v) => upd((b) => ({ ...b, areas: { ...b.areas, [k]: v } }))} testid={`cms-area-${k}`} />)}
      </div>
    </section>
  );
};

const toLocal = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("sv-SE", { timeZone: "Europe/Rome" }).replace(" ", "T").slice(0, 16);
};
const EMPTY = { title: "", text: "", image_url: "", cta_label: "", cta_url: "", starts_at: "", ends_at: "", active: true, show_countdown: true };

const PromoForm = ({ initial, onDone }) => {
  const [f, setF] = useState(initial.promo_id ? { show_countdown: true, ...initial, starts_at: toLocal(initial.starts_at), ends_at: toLocal(initial.ends_at) } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { title: f.title, text: f.text, image_url: f.image_url, cta_label: f.cta_label, cta_url: f.cta_url, active: f.active, show_countdown: f.show_countdown,
      starts_at: romeToIso(f.starts_at), ends_at: romeToIso(f.ends_at) };
    try {
      if (initial.promo_id) await api.put(`/admin/promos/${initial.promo_id}`, body); else await api.post("/admin/promos", body);
      toast.success("Promo salvata"); onDone();
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mt-5 grid gap-4 rounded-2xl bg-[#F8F9FC] p-5 lg:grid-cols-[1fr_260px]" data-testid="promo-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2"><label className="label">Titolo</label><input required minLength={2} maxLength={80} className="field" value={f.title} onChange={set("title")} placeholder="Black Friday Juppo" data-testid="promo-title-input" /></div>
        <div className="sm:col-span-2"><label className="label">Testo</label><textarea maxLength={300} rows={3} className="field" value={f.text} onChange={set("text")} data-testid="promo-text-input" /></div>
        <div><label className="label">Testo del pulsante</label><input maxLength={40} className="field" value={f.cta_label} onChange={set("cta_label")} placeholder="Scopri le offerte" data-testid="promo-cta-label-input" /></div>
        <div><label className="label">Link del pulsante</label><input className="field" value={f.cta_url} onChange={set("cta_url")} placeholder="/cerca?deals=true" data-testid="promo-cta-url-input" /></div>
        <div><label className="label">Inizio (ora italiana)</label><input required type="datetime-local" className="field" value={f.starts_at} onChange={set("starts_at")} data-testid="promo-start-input" /></div>
        <div><label className="label">Fine (ora italiana)</label><input required type="datetime-local" className="field" value={f.ends_at} onChange={set("ends_at")} data-testid="promo-end-input" /></div>
        <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="promo-active-checkbox" />Attiva</label>
        <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={f.show_countdown} onChange={(e) => setF({ ...f, show_countdown: e.target.checked })} data-testid="promo-countdown-checkbox" />Mostra conto alla rovescia fino alla fine</label>
        <div className="flex gap-2 sm:col-span-2"><button disabled={busy} className="btn-violet" data-testid="promo-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Salva promo</button>
          <button type="button" onClick={onDone} className="btn-ghost" data-testid="promo-cancel-btn">Annulla</button></div>
      </div>
      <ImagePick tall label="Immagine (facoltativa)" value={f.image_url} onChange={(v) => setF({ ...f, image_url: v })} testid="promo-image" />
    </form>
  );
};

const status = (p) => {
  const now = new Date().toISOString();
  if (!p.active) return ["Disattivata", "bg-slate-100 text-slate-600"];
  if (p.ends_at < now) return ["Scaduta", "bg-slate-100 text-slate-600"];
  if (p.starts_at > now) return ["Programmata", "bg-amber-50 text-amber-700"];
  return ["In corso", "bg-emerald-50 text-emerald-700"];
};

const Promos = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-promos"], queryFn: async () => (await api.get("/admin/promos")).data });
  const [edit, setEdit] = useState(null);
  const done = () => { setEdit(null); qc.invalidateQueries({ queryKey: ["admin-promos"] }); };
  const del = async (p) => {
    if (!window.confirm(`Eliminare la promo "${p.title}"?`)) return;
    try { await api.delete(`/admin/promos/${p.promo_id}`); done(); toast.success("Promo eliminata"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="card-soft p-6" data-testid="admin-promos">
      <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-lg font-bold">Pop-up promo</h2>
        <p className="mt-1 text-sm text-[#64748B]">Compare a ogni visita del sito tra la data di inizio e quella di fine. Se più promo sono in corso, si mostra la più recente.</p></div>
        {!edit && <button onClick={() => setEdit({})} className="btn-violet" data-testid="promo-new-btn">Nuova promo</button>}</div>
      {edit && <PromoForm key={edit.promo_id || "new"} initial={edit} onDone={done} />}
      <div className="mt-5"><Table testid="promos-table" head={["Titolo", "Periodo", "Stato", ""]}>
        {data?.items.map((p) => { const [lbl, cls] = status(p); return (
          <tr key={p.promo_id} className="border-t border-[#EEF0F6]" data-testid={`promo-row-${p.promo_id}`}>
            <td className="p-3 font-semibold">{p.title}</td>
            <td className="p-3 text-xs">{formatDate(p.starts_at)} → {formatDate(p.ends_at)}</td>
            <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${cls}`}>{lbl}</span></td>
            <td className="p-3 text-right"><button onClick={() => setEdit(p)} className="p-1.5" data-testid={`promo-edit-${p.promo_id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(p)} className="p-1.5 text-red-600" data-testid={`promo-delete-${p.promo_id}`}><Trash2 className="h-4 w-4" /></button></td>
          </tr>); })}
        {data?.items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-sm text-[#64748B]">Nessuna promo creata</td></tr>}
      </Table></div>
    </section>
  );
};

export default function ContentPage() {
  return (
    <div className="space-y-6" data-testid="admin-content">
      <H title="Home e promo" sub="Immagini della home page e pop-up promozionali" />
      <HomeImages />
      <Promos />
    </div>
  );
}
